import { useEffect, useState, type ReactNode } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents } from '@/features/catalog/catalog';
import { colorDe } from '@/ui/ColorParticipante';
import { listAllParticipants, type Participant } from '@/features/works/works';
import {
  cantidadesParaFacturar,
  fetchRepartoDelMes,
  tocaEnElTrabajo,
  totalesPorPersona,
  type RepartoDelMes,
} from './reparto';

/**
 * El reparto del mes, trabajo por trabajo.
 *
 * ## Para qué existe
 *
 * Mientras se corren la aplicación y la planilla de siempre en paralelo —que es
 * lo que corresponde hasta confiar en la primera— el total del mes no alcanza.
 * Un total que no coincide dice que hay un error; no dice cuál. Encontrarlo
 * obligaba a abrir los trabajos de a uno y rehacer la cuenta a mano.
 *
 * Acá está cada trabajo con lo que le tocó a cada uno, y el mismo total abajo.
 * Se contrasta renglón contra renglón y la diferencia aparece sola. Es la misma
 * forma en que se reconcilió el mes patrón: la diferencia estaba en un renglón
 * de un solo trabajo, y en pesos parecía un redondeo.
 *
 * ## Se ve durante el mes, no al cerrarlo
 *
 * La liquidación muestra la distribución recién cuando el período está cerrado,
 * y cerrar congela. Esperar a ese momento para revisar es revisar cuando ya no
 * se puede corregir sin reabrir. Esto es la misma cuenta, en vivo: cargar un
 * trabajo más y volver lo muestra sumado.
 *
 * ## Por qué dejó de ser una pantalla aparte
 *
 * Estaba detrás de un enlace subrayado al pie del resumen, y ahí no lo
 * encontraba nadie: es lo que se usa para revisar contra la planilla, o sea
 * casi todos los días mientras las dos convivan. Un paso de navegación de más
 * para lo que más se mira.
 *
 * Ahora va DEBAJO del resumen, en la misma pantalla y a un scroll de
 * distancia. Arriba queda la pregunta —cuánto ganó cada uno— y abajo, en el
 * lugar donde se busca cuando el número no cierra, de qué trabajo salió.
 *
 * ## También es donde se arma la factura
 *
 * Con el tiempo esta pantalla pasó a usarse para otra cosa: facturar. Es el
 * único lugar donde están todos los trabajos del mes de un cliente juntos, y
 * facturar no se hace con el total sino con los renglones —cuántos tactos,
 * cuántas sincronizaciones, cuántos kilómetros—. Esos números estaban adentro
 * de cada trabajo, de a uno, así que había que abrirlos y anotar a mano.
 *
 * Por eso cada tarjeta muestra ahora las cantidades, y por eso no se truncan:
 * un «Sincronizar para IATF…» cortado es justo el renglón que se vino a leer.
 *
 * ## El mes es el eje, y por eso "vuelve a cero" solo
 *
 * No hay nada que reiniciar al cerrar un mes. Cada trabajo pertenece a su mes
 * de facturación y la pantalla muestra un mes por vez: pasar a octubre muestra
 * octubre, que empieza vacío. Un botón de "reiniciar" sería una forma de
 * romperlo.
 */
interface Props {
  onVerTrabajo: (workId: string) => void;
}

export function RepartoDelMes({ onVerTrabajo }: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  const { year, month } = usePeriod();

  const [data, setData] = useState<RepartoDelMes | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Para poder pintar cada nombre con su color.
   *
   * El color es lo que permite revisar el mes SIN LEER: se recorre la columna
   * y se ve quién factura, quién se queda con la plata y quién cobró el viaje.
   * Leyendo tres nombres por trabajo, veinte trabajos son sesenta lecturas.
   */
  const [gente, setGente] = useState<Participant[]>([]);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      setCargando(true);
      try {
        const d = await fetchRepartoDelMes(orgId, year, month);
        if (cancelado) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (cancelado) return;
        setError(mensajeDeError(e));
        setData(null);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  // Aparte y en silencio: si falla, el reparto se ve igual, sin colores. Son
  // un atajo para la vista, nunca la única forma de saber quién es quién —los
  // nombres están escritos al lado—.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const l = await listAllParticipants(orgId);
        if (!cancelado) setGente(l);
      } catch {
        // Sin colores se lee igual.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  /** El color configurado de una persona, o `null` si no eligió ninguno. */
  const colorDeQuien = (pid: string | null) =>
    pid ? colorDe(gente.find((g) => g.id === pid)?.color) : null;

  /** Su nombre. La cuenta técnica se dice como lo que es. */
  const nombreDeQuien = (pid: string | null) => {
    const p = gente.find((g) => g.id === pid);
    if (!p) return null;
    return p.kind === 'org_account' ? `${p.display_name} (la veterinaria)` : p.display_name;
  };

  if (!activeOrg || !orgId) return null;

  const totales = data ? totalesPorPersona(data) : [];
  const totalMes = data?.works.reduce((a, w) => a + (w.total_cents ?? 0), 0) ?? 0;

  return (
    <div className="flex flex-col gap-3" data-testid="reparto-page">
      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="reparto-error">
          {error}
        </p>
      )}

      {cargando && !data && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

      {data && data.works.length > 0 && (
        <>
          {/* El total por persona va ARRIBA: es lo que se viene a comparar
              contra la planilla. El detalle está abajo para cuando no coincida. */}
          <section className="flex flex-col superficie" data-testid="reparto-totales">
            <h2 className="border-b border-[var(--linea)] px-4 py-2 text-xs font-medium uppercase opacity-60">
              Trabajo por trabajo · le tocó a cada uno
            </h2>
            <ul className="divide-y divide-[var(--linea)]">
              {totales.map((t) => (
                <li
                  key={t.participant_id}
                  className="flex items-baseline justify-between gap-3 px-4 py-2 text-sm"
                  data-testid="reparto-total-persona"
                >
                  <span className="min-w-0 truncate">{t.display_name}</span>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {formatCents(t.cents)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex items-baseline justify-between gap-3 border-t border-[var(--linea)] px-4 py-2 text-sm">
              <span className="opacity-70">
                {data.works.length} trabajo{data.works.length === 1 ? '' : 's'}
              </span>
              <span className="font-semibold tabular-nums" data-testid="reparto-total-mes">
                {formatCents(totalMes)}
              </span>
            </div>
            {/* Los ajustes no viven en ningún trabajo: son asientos del mes. Sin
                esta línea, la diferencia entre este total y el del resumen se
                descubre restando, que es la peor forma de enterarse. */}
            {data.adjustments_count > 0 && (
              <p
                className="px-4 pb-2 text-xs text-[var(--aviso)]"
                data-testid="reparto-nota-ajustes"
              >
                El mes tiene {data.adjustments_count} ajuste
                {data.adjustments_count === 1 ? '' : 's'} que no pertenecen a ningún trabajo. Este
                total es lo repartido antes de ellos.
              </p>
            )}
          </section>

          {/* --- Trabajo por trabajo --- */}
          <ul className="flex flex-col gap-2" data-testid="reparto-lista">
            {data.works.map((w) => {
              const cantidades = cantidadesParaFacturar(w);
              const colorFactura = colorDeQuien(w.billed_to_participant_id);
              return (
                <li key={w.work_session_id}>
                  <button
                    type="button"
                    onClick={() => onVerTrabajo(w.work_session_id)}
                    className="flex w-full flex-col gap-1 rounded-[var(--radius-superficie)] bg-[var(--superficie)] p-3 text-left"
                    data-testid="reparto-trabajo"
                  >
                    {/* El nombre del cliente va PINTADO con el color de quien lo
                      factura. Es el renglón más grande de la tarjeta y el que
                      la vista busca sola al bajar la lista: con eso, saber de
                      quién es cada factura deja de costar una lectura por
                      trabajo.

                      Sigue siendo un atajo y no el dato: el nombre de quien
                      factura está escrito abajo, en la línea de estado. */}
                    <div className="flex items-baseline justify-between gap-3">
                      <span
                        className="truncate font-semibold leading-tight"
                        style={colorFactura ? { color: colorFactura } : undefined}
                        data-testid="reparto-cliente"
                      >
                        {w.client_name}
                      </span>
                      <span
                        className={`shrink-0 font-semibold tabular-nums ${
                          w.total_cents === null ? 'text-[var(--aviso)]' : ''
                        }`}
                      >
                        {formatCents(w.total_cents)}
                      </span>
                    </div>

                    {w.establishment_name && (
                      <span className="truncate text-sm text-[var(--tinta-suave)]">
                        {w.establishment_name}
                      </span>
                    )}

                    <span className="text-xs text-[var(--tinta-tenue)]">
                      {new Date(`${w.work_date}T00:00:00`).toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                      {w.total_kg ? ` · ${w.total_kg} kg` : ''}
                      {/* Los nombres sueltos son el respaldo para cuando el
                        servidor todavía no devuelve las cantidades. Con ellas,
                        el bloque de abajo dice lo mismo y mejor. */}
                      {cantidades.length === 0 && w.service_names ? ` · ${w.service_names}` : ''}
                    </span>

                    {/* --- Con qué se factura -----------------------------------
                      Cuántos tactos, cuántas sincronizaciones, cuántos
                      kilómetros. Antes estaban adentro de cada trabajo, de a
                      uno, y había que abrirlos para armar la factura de un
                      cliente que tiene cinco trabajos en el mes.

                      No se truncan. Un «Sincronizar para IATF…» cortado es
                      exactamente el renglón que se vino a leer.
                     -------------------------------------------------------- */}
                    {cantidades.length > 0 && (
                      <span
                        className="mt-1 flex flex-col gap-0.5 text-xs"
                        data-testid="reparto-cantidades"
                      >
                        {cantidades.map((c, i) => (
                          <span
                            key={`${c.nombre}-${i}`}
                            className="flex items-baseline justify-between gap-3"
                            data-testid="reparto-cantidad"
                          >
                            <span className="min-w-0 text-[var(--tinta-suave)]">{c.nombre}</span>
                            {c.cantidad !== null ? (
                              <span className="shrink-0 font-medium tabular-nums">
                                {c.cantidad}
                              </span>
                            ) : (
                              c.dudosa && (
                                <span className="shrink-0 text-[var(--aviso)]">
                                  cantidad sin confirmar
                                </span>
                              )
                            )}
                          </span>
                        ))}
                      </span>
                    )}

                    {/* Quién se queda con la plata y quién cobró el viaje, por
                      separado: en un mismo trabajo pueden ser dos personas
                      distintas, y una tercera la que factura.

                      El nombre VA PINTADO con el color de esa persona, y no
                      hay punto al lado. Con las dos cosas juntas el renglón se
                      pisaba —«Movilidad» no entraba en la columna del rótulo y
                      se montaba sobre el círculo— y además decían lo mismo dos
                      veces.

                      Pintar el nombre en vez de dejar sólo el punto no es una
                      concesión: quien no eligió color, quien recién entra al
                      equipo y quien no distingue dos tonos tienen que poder
                      revisar igual. El color acelera; el nombre es lo que
                      informa. Los ocho de la paleta se midieron sobre este
                      fondo y todos pasan AA como texto. */}
                    <span className="mt-1 flex flex-col gap-1 border-t border-[var(--linea)] pt-1.5 text-xs">
                      {/* La movilidad sólo si la hay, y con NOMBRE: es de quien
                        viajó, que muchas veces no es quien factura. */}
                      {w.reparto
                        .filter((r) => r.mobility_cents !== 0)
                        .map((r) => (
                          <span
                            key={`mov-${r.participant_id}`}
                            className="flex items-baseline gap-2"
                            data-testid="reparto-movilidad"
                          >
                            <span className="w-20 shrink-0 text-[var(--tinta-tenue)]">
                              Movilidad
                            </span>
                            <Nombre color={colorDeQuien(r.participant_id)}>{r.display_name}</Nombre>
                            <span className="ml-auto shrink-0 tabular-nums text-[var(--tinta-suave)]">
                              {formatCents(r.mobility_cents)}
                            </span>
                          </span>
                        ))}

                      {w.reparto.length === 0 ? (
                        <span className="text-[var(--aviso)]">
                          Sin reparto cargado: este trabajo no le suma a nadie.
                        </span>
                      ) : (
                        w.reparto.map((r, i) => (
                          <span
                            key={r.participant_id}
                            className="flex items-baseline gap-2"
                            data-testid="reparto-linea"
                          >
                            {/* El rótulo va una sola vez, en la primera línea.
                              Repetirlo en cada una convierte una columna
                              prolija en un muro de palabras. */}
                            <span className="w-20 shrink-0 text-[var(--tinta-tenue)]">
                              {i === 0 ? 'Le toca a' : ''}
                            </span>
                            <Nombre color={colorDeQuien(r.participant_id)}>{r.display_name}</Nombre>
                            <span className="ml-auto shrink-0 font-medium tabular-nums">
                              {formatCents(tocaEnElTrabajo(r))}
                            </span>
                          </span>
                        ))
                      )}
                    </span>

                    {/* El estado y QUIÉN FACTURA, en la misma línea.

                      Antes eran dos renglones: uno decía «Factura · Manuel» y
                      otro, abajo, «○ Facturado». Dicen lo mismo con el verbo
                      conjugado —«factura Manuel» / «facturó Manuel»— y ahí el
                      estado y la persona entran juntos, que es como se leen:
                      nadie pregunta si está facturado sin preguntar de quién.

                      Esta línea es la que hace que pintar el nombre del cliente
                      arriba sea un atajo y no el único portador del dato. */}
                    <span className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                      <span className="flex items-baseline gap-1" data-testid="reparto-factura">
                        <span
                          className={
                            w.is_billed
                              ? 'font-medium text-[var(--accion)]'
                              : 'text-[var(--tinta-tenue)]'
                          }
                        >
                          {w.is_billed ? '✓ Facturó' : '○ Factura'}
                        </span>
                        <Nombre color={colorFactura}>
                          {nombreDeQuien(w.billed_to_participant_id) ?? 'Sin asignar'}
                        </Nombre>
                      </span>
                      <span
                        className={
                          w.is_paid
                            ? 'font-medium text-[var(--exito)]'
                            : 'text-[var(--tinta-tenue)]'
                        }
                      >
                        {w.is_paid ? '✓' : '○'} Cobrado
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * El nombre de una persona, pintado con su color.
 *
 * Reemplazó al punto de color con el nombre al lado. Los dos juntos decían lo
 * mismo dos veces y no entraban: en un teléfono, «Movilidad» más un círculo
 * más un nombre más un importe se montaban unos sobre otros.
 *
 * Sin color elegido queda con la tinta normal, que se lee igual. El color es un
 * atajo para la vista, nunca el único portador del dato: el nombre está
 * escrito, y eso es lo que hace que la pantalla siga sirviendo para quien
 * todavía no eligió colores o no distingue dos de ellos.
 */
function Nombre({ color, children }: { color: string | null; children: ReactNode }) {
  return (
    <span
      className="truncate font-medium"
      style={color ? { color } : undefined}
      data-testid="reparto-nombre"
    >
      {children}
    </span>
  );
}
